import React, { Fragment } from 'react';
import { Button, InputText, Select } from '@/components';
import { AuthWrapper } from '@/features/core/auth/screens/AuthWrapper';
import { OptionProps } from '@/components/Select';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { FormInputNameCreditCard } from '../../types';

interface StateProps {
  willValidate: boolean;
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onAddCreditCard: () => void;
}

type Props = StateProps & DispatchProps;

export const AddCreditCardUI: React.FC<Props> = ({
  willValidate,
  formData,
  onChangeFormInput,
  formErrors,
  onAddCreditCard,
}) => {
  const month: OptionProps[] = [
    { value: '01', label: '01' },
    { value: '02', label: '02' },
    { value: '03', label: '03' },
    { value: '04', label: '04' },
    { value: '05', label: '05' },
    { value: '06', label: '06' },
    { value: '07', label: '07' },
    { value: '08', label: '08' },
    { value: '09', label: '09' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
  ];

  const year: OptionProps[] = [
    { value: '2026', label: '26' },
    { value: '2027', label: '27' },
    { value: '2028', label: '28' },
    { value: '2029', label: '29' },
    { value: '2030', label: '30' },
    { value: '2031', label: '31' },
    { value: '2032', label: '32' },
    { value: '2033', label: '33' },
    { value: '2034', label: '34' },
    { value: '2035', label: '35' },
    { value: '2036', label: '36' },
    { value: '2037', label: '37' },
    { value: '2038', label: '38' },
    { value: '2039', label: '39' },
    { value: '2040', label: '40' },
    { value: '2041', label: '41' },
    { value: '2042', label: '42' },
    { value: '2043', label: '43' },
    { value: '2044', label: '44' },
    { value: '2045', label: '45' },
    { value: '2046', label: '46' },
    { value: '2047', label: '47' },
    { value: '2048', label: '48' },
    { value: '2049', label: '49' },
    { value: '2050', label: '50' },
  ];
  return (
    <AuthWrapper isHomepage={false}>
      <div className="w-full container mx-auto pt-5 pb-24">
        <div className="flex flex-col md:flex-row gap-8">
          <aside className="md:w-1/3 w-full bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
            <h3 className="text-2xl font-semibold text-neutro-b-400 mb-4">
              Passo a passo para adicionar seu cartão
            </h3>
            <ul className="space-y-4 font-dmsans text-sm text-neutro-w-600 leading-relaxed">
              <li className="flex items-start">
                <span className="text-primary-500 font-bold mr-2">1.</span>
                Digite os dados do cartão com atenção: número, validade, CVV, nome e CPF do titular.
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 font-bold mr-2">2.</span>
                Certifique-se de que as informações estão idênticas às do cartão físico.
              </li>
              {willValidate && (
                <Fragment>
                  <li className="flex items-start">
                    <span className="text-primary-500 font-bold mr-2">3.</span>
                    Será realizada uma validação com uma cobrança simbólica temporária.
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-500 font-bold mr-2">4.</span>
                    No próximo passo, você deverá informar o valor exato dessa cobrança na fatura do
                    seu cartão para confirmar o cartão.
                  </li>
                </Fragment>
              )}
            </ul>
          </aside>
          <section className="md:w-2/3 w-full">
            <div className="bg-white shadow-md rounded-2xl p-6 md:p-10">
              <h2 className="font-dmsans font-normal text-neutro-b-400 text-[28px] mb-2">
                Cadastrar novo cartão
              </h2>
              <div className="w-full mt-[20px]">
                <div className="mb-[40px]">
                  <InputText
                    name="number"
                    label="Número do cartão"
                    placeholder="1234 5678 90123 3456"
                    className={`appearance-none w-full
              rounded-md text-heading leading-tight focus:outline-none
              focus:shadow-outline text-xs font-dmsans font-normal`}
                    value={formData[FormInputNameCreditCard.number]}
                    onChange={e =>
                      onChangeFormInput(FormInputNameCreditCard.number)(e.target.value)
                    }
                    error={formErrors.number && formErrors.number[0]}
                  />
                </div>
                <div className="mb-5 grid grid-cols-12 gap-4">
                  <Select
                    name="month"
                    label="Mês"
                    placeholder="Selecione..."
                    value={formData[FormInputNameCreditCard.month]}
                    onChange={e =>
                      onChangeFormInput(FormInputNameCreditCard.month)(e?.target.value as string)
                    }
                    className="col-span-4"
                    error={formErrors.month && formErrors.month[0]}
                    options={month}
                  />

                  <Select
                    name="year"
                    label="Ano"
                    placeholder="Selecione..."
                    value={formData[FormInputNameCreditCard.year]}
                    onChange={e =>
                      onChangeFormInput(FormInputNameCreditCard.year)(e?.target.value as string)
                    }
                    className="col-span-4"
                    error={formErrors.year && formErrors.year[0]}
                    options={year}
                  />

                  <InputText
                    name="cvv"
                    label="CVV"
                    placeholder="000"
                    className="col-span-4 appearance-none w-full
      rounded-md text-heading leading-tight focus:outline-none
      focus:shadow-outline text-xs font-dmsans font-normal"
                    value={formData[FormInputNameCreditCard.cvv]}
                    onChange={e => onChangeFormInput(FormInputNameCreditCard.cvv)(e.target.value)}
                    error={formErrors.cvv && formErrors.cvv[0]}
                  />
                </div>
                <div className="mb-[20px]">
                  <InputText
                    name="name"
                    label="Nome do títular"
                    placeholder="Maria da Silva"
                    className={`appearance-none w-full
              rounded-md text-heading leading-tight focus:outline-none
              focus:shadow-outline text-xs font-dmsans font-normal`}
                    value={formData[FormInputNameCreditCard.name]}
                    onChange={e => onChangeFormInput(FormInputNameCreditCard.name)(e.target.value)}
                    error={formErrors.name && formErrors.name[0]}
                  />
                </div>
                <div className="mb-[20px]">
                  <InputText
                    name="document"
                    label="CPF do títular"
                    placeholder="123.456.789-00"
                    className={`appearance-none w-full
              rounded-md text-heading leading-tight focus:outline-none
              focus:shadow-outline text-xs font-dmsans font-normal`}
                    maxLength={14}
                    value={formData[FormInputNameCreditCard.document]}
                    onChange={e =>
                      onChangeFormInput(FormInputNameCreditCard.document)(e.target.value)
                    }
                    error={formErrors.document && formErrors.document[0]}
                  />
                </div>
                <div className="mb-[0px]">
                  <Button
                    type="submit"
                    title="Adicionar cartão"
                    buttonStyle="primary"
                    size="xlg"
                    className="w-full"
                    disabled={
                      formData[FormInputNameCreditCard.number] === '' ||
                      formData[FormInputNameCreditCard.month] === '' ||
                      formData[FormInputNameCreditCard.year] === '' ||
                      formData[FormInputNameCreditCard.cvv] === '' ||
                      formData[FormInputNameCreditCard.name] === '' ||
                      formData[FormInputNameCreditCard.document] === ''
                    }
                    onClick={() => onAddCreditCard()}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AuthWrapper>
  );
};
